import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  Suspense,
  lazy,
} from "react";
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { isViewerMember } from "./utils/entityType";

// Viewer Members may view a tender but never reach the offer form, even by direct URL.
const ViewerMemberOfferGuard = ({ user, children }) => {
  const { recId } = useParams();
  if (isViewerMember(user)) {
    return <Navigate to={recId ? `/tender/${recId}/view` : "/dashboard"} replace />;
  }
  return children;
};
import { useDispatch, useSelector } from "react-redux";
import { fetchUser } from "@/modules/auth/store/authSlice";
import { isNonSaudiApplicantUser } from "./modules/registration/utils/nonSaudiApplicant";
import { flowRedirect, redirectMatchesPath } from "./modules/subscriptions/utils/registrationFlow";
import {
  addGlobalAlert,
  removeGlobalAlert,
  setLoading,
  setProgress,
} from "@/modules/shared/store/uiSlice";
import "./App.css";

import Navbar from "./components/Navbar";
import PrivateRoute from "./components/PrivateRoute";
import { logout as logoutThunk } from "@/modules/auth/store/authSlice";
import SkeletonLoader from "./components/SkeletonLoader";
import ServicesDashboardSkeleton from "./modules/services/components/ServicesDashboardSkeleton";
import GlobalLoader from "./components/GlobalLoader";
import AttachmentViewer from "./components/AttachmentViewer";
import i18n from "./i18n";
import { useTranslation } from "react-i18next";
import AlertBar from "./components/AlertBar";
import SupportSessionBanner from "./components/SupportSessionBanner";
import SupportHandoffPage from "./components/SupportHandoffPage";
import SupportEndedPage from "./components/SupportEndedPage";

// import { checkProfile } from "./store/slices/companyInfoSlice";

import Footer from "./modules/shared/components/Footer/Footer";
import { SaudiRiyal } from "lucide-react";
import { fetchNotifications } from "@/modules/notifications/store/notificationsSlice";
import ScrollToTop from "./helper/ScrollToTop";
import MobileBottomNav from "./components/MobileBottomNav";
import PullToRefresh from "./components/PullToRefresh";
import {
  checkSubscription,
  ensureSubscriptionStatus,
  fetchSubscriptions,
} from "@/modules/subscriptions/store/subscriptionSlice";
import { fetchAnnouncements } from "@/modules/shared/store/announcementSlice";
import { fetchFeatures } from "@/modules/shared/store/featuresSlice";
import api from "@/modules/shared/api/axios";
import Swal from "sweetalert2";
import ModuleAccessGuard from "./components/ModuleAccessGuard";
import {
  SERVICE_MODULE_KEYS,
  getServiceModuleName,
  FREE_MODULE_KEYS,
} from "./config/serviceModules";
import { readPaymentContext } from "./modules/subscriptions/utils/paymentContext";

// Lazy load pages
const Login = lazy(() => import("./modules/auth/pages/Login"));
const Register = lazy(() => import("./modules/auth/pages/Register"));
const CompanyTypeSelectionPage = lazy(() => import("./modules/registration/pages/CompanyTypeSelectionPage"));
const NonSaudiRegistrationPage = lazy(() => import("./modules/registration/pages/NonSaudiRegistrationPage"));
const NonSaudiEmailVerificationCompletePage = lazy(() => import("./modules/registration/pages/NonSaudiEmailVerificationCompletePage"));
const RegistrationStatusPage = lazy(() => import("./modules/registration/pages/RegistrationStatusPage"));
const NewRegistrationRequestPage = lazy(() => import("./modules/registration/pages/NewRegistrationRequestPage"));
const Forgot = lazy(() => import("./modules/auth/pages/Forgot"));
const Reset = lazy(() => import("./modules/auth/pages/Reset"));
const Dashboard = lazy(() =>
  import("./modules/tenders/pages/TendersDashboard/Dashboard")
);
const OffersDashboard = lazy(() =>
  import("./modules/offers/pages/OffersDashboard/OffersDashboard")
);
const ServicesDashboard = lazy(() =>
  import("./modules/services/pages/ServicesDashboard")
);
const SubscriptionPage = lazy(() =>
  import("./modules/subscriptions/pages/SubscriptionPage")
);
const UpgradePlansPage = lazy(() =>
  import("./modules/subscriptions/pages/UpgradePlansPage")
);
import BankTransferPage from "./modules/subscriptions/pages/BankTransferPage";
const LandingPage = lazy(() => import("./modules/services/pages/LandingPage"));

const ALLOW_SUBSCRIBE_AFTER_REJECT_KEY = "allowSubscribeAfterRejection";

const TenderForm = lazy(() =>
  import("./modules/tenders/pages/TenderForm/TenderForm")
);
const TenderDashboard = lazy(() =>
  import("./modules/tenders/pages/TenderDashboard")
);
const AllTendersLanding = lazy(() =>
  import("./modules/tenders/pages/AllTendersLanding")
);
const TenderInfo = lazy(() =>
  import("./modules/tenders/pages/TenderInfo/TenderInfo")
);
const ReceivedProposal = lazy(() =>
  import("./modules/approvals/pages/ReceivedProposals")
);
const IncomingOffersPriceAnalysisDashboard = lazy(() =>
  import(
    "./modules/incoming-offers-price-analysis/pages/IncomingOffersPriceAnalysisDashboard"
  )
);
const PreferredSuppliers = lazy(() =>
  import("./modules/tenders/pages/PreferredSuppliers")
);
const NotificationPage = lazy(() =>
  import("./modules/notifications/pages/NotificationPage")
);
const OffersAnalysisPage = lazy(() =>
  import("./modules/tenders/pages/OffersAnalysisPage")
);
const ProfilePage = lazy(() =>
  import("./modules/profile/pages/ProfilePage")
);
const RechargeWalletPage = lazy(() =>
  import("./modules/wallet/pages/RechargeWalletPage")
);
const WalletRechargePaymentPage = lazy(() => import("./modules/wallet/pages/WalletRechargePaymentPage"));
const WalletRechargeResultPage = lazy(() => import("./modules/wallet/pages/WalletRechargeResultPage"));
const WalletSadadPage = lazy(() => import("./modules/wallet/pages/WalletSadadPage"));
const CreateDelegate = lazy(() =>
  import("./modules/delegation/pages/CreateDelegate")
);
const PermissionManagement = lazy(() =>
  import("./modules/delegation/pages/PermissionManagement")
);
const IncomingInvitations = lazy(() =>
  import("./modules/proposal-approvals/pages/IncomingInvitations")
);
const IncomingParticipationRequests = lazy(() =>
  import(
    "./modules/approvals/pages/IncomingParticipationRequests"
  )
);
const IncomingNegotiations = lazy(() =>
  import("./modules/negotiation-award/pages/IncomingNegotiations")
);
const PublisherNegotiationResponses = lazy(() =>
  import("./modules/negotiation-award/pages/PublisherNegotiationResponses")
);
const NegotiationDashboard = lazy(() =>
  import("./modules/negotiation-award/pages/NegotiationDashboard")
);
const PublisherNegotiationResponseDetails = lazy(() =>
  import("./modules/negotiation-award/pages/PublisherNegotiationResponseDetails")
);
const NegotiationStage = lazy(() =>
  import("./modules/negotiation-award/pages/NegotiationStage")
);
const NegotiationOfferDetails = lazy(() =>
  import("./modules/negotiation-award/pages/NegotiationOfferDetails")
);
const TenderAwarding = lazy(() =>
  import("./modules/negotiation-award/pages/TenderAwarding")
);
const ImportingEntitySelection = lazy(() =>
  import("./modules/negotiation-award/pages/ImportingEntitySelection")
);
const ImportRequestsList = lazy(() =>
  import("./modules/importing-entities/pages/ImportRequestsList")
);
const ImportRequestForm = lazy(() =>
  import("./modules/importing-entities/pages/ImportRequestForm")
);
const ImportRequestDetails = lazy(() =>
  import("./modules/importing-entities/pages/ImportRequestDetails")
);
const TenderAwardingList = lazy(() =>
  import("./modules/negotiation-award/pages/TenderAwardingList")
);
const FinancialClaims = lazy(() =>
  import("./modules/financial-claims/pages/FinancialClaims")
);

const FinancialClaimsDashboard = lazy(() =>
  import("./modules/financial-claims/pages/FinancialClaimsDashboard")
);
const FinancialClaimForm = lazy(() =>
  import("./modules/financial-claims/pages/FinancialClaimForm")
);
const IncomingFinancialClaims = lazy(() =>
  import("./modules/financial-claims/pages/IncomingFinancialClaims")
);
const AwardedTendersToMe = lazy(() =>
  import("./modules/financial-claims/pages/AwardedTendersToMe")
);
const BankAccountManagement = lazy(() =>
  import("./modules/financial-claims/pages/BankAccountManagement")
);
const IncomingFinancialClaimDetails = lazy(() =>
  import("./modules/financial-claims/pages/IncomingFinancialClaimDetails")
);
const TenderApprovals = lazy(() =>
  import("./modules/approvals/pages/TenderApprovals")
);

const PaymentWidget = lazy(() =>
  import("./modules/subscriptions/pages/pay/PaymentWidget")
);
const ApplePayPage = lazy(() =>
  import("./modules/subscriptions/pages/pay/ApplePayPage")
);
const SadadPayPage = lazy(() =>
  import("./modules/subscriptions/pages/sadad/SadadPayPage")
);
const SadadPaymentResult = lazy(() =>
  import("./modules/subscriptions/pages/sadad/SadadPaymentResult")
);
const PaymentResult = lazy(() =>
  import("./modules/subscriptions/pages/pay/PaymentResult")
);
const ApplePayResult = lazy(() =>
  import("./modules/subscriptions/pages/pay/ApplePayResult")
);
const VatPage = lazy(() =>
  import("./modules/subscriptions/pages/vat/VatPage")
);
const DeclarationGuardPage = lazy(() =>
  import("./modules/subscriptions/pages/declarations/DeclarationGuardPage")
);
const DeclarationPendingPage = lazy(() =>
  import("./modules/subscriptions/pages/declarations/DeclarationPendingPage")
);
const FieldOfWorkClassificationPage = lazy(() =>
  import("./modules/work-classification/pages/FieldOfWorkClassificationPage")
);
const CompanyListPage = lazy(() =>
  import("./modules/companies/pages/CompanyListPage")
);
const ImportingEntitiesListPage = lazy(() =>
  import("./modules/companies/pages/CompanyListPage")
);
const PremiumSubscribersList = lazy(() =>
  import("./modules/companies/pages/PremiumSubscribersList")
);
const AuthorizationPendingPage = lazy(() =>
  import("./modules/auth/pages/AuthorizationPendingPage")
);
const ModulePlaceholderPage = lazy(() =>
  import("./modules/shared/pages/ModulePlaceholderPage")
);
const ExternalRedirectPage = lazy(() =>
  import("./modules/shared/pages/ExternalRedirectPage")
);
const CommunicationPage = lazy(() =>
  import("./modules/communication/pages/Communication")
);
const SubscriptionManagementPage = lazy(() =>
  import("./modules/subscriptions/pages/SubscriptionManagementPage")
);
const ContractsDashboard = lazy(() =>
  import("./modules/contracts/pages/ContractsDashboard")
);
const ContractForm = lazy(() =>
  import("./modules/contracts/pages/ContractForm")
);
const IssuedContracts = lazy(() =>
  import("./modules/contracts/pages/IssuedContracts")
);
const IncomingContracts = lazy(() =>
  import("./modules/contracts/pages/IncomingContracts")
);
const ApprovedContracts = lazy(() =>
  import("./modules/contracts/pages/ApprovedContracts")
);
const ContractsApprovals = lazy(() =>
  import("./modules/contracts/pages/ContractsApprovals")
);

const ApprovalDashboard = lazy(() => {
  return import("./modules/approvals/pages/ApprovalDashboard")
})

const ProposalDashboard = lazy(() => {
  return import("./modules/proposal-approvals/pages/ProposalDashboard")
})

const DelegationDashboard = lazy(() => {
  return import("./modules/delegation/pages/DelegationDashboard")
})

const APPLICANT_STATUS_PATH = "/registration/status";
// Where signed-in users land after login: the All Tenders landing page, shown with the public navbar links.
// The Services page (/dashboard) stays the fallback for anyone the existing access checks don't let in.
const HOME_PATH = "/tenders";
const APPLICANT_REQUEST_PATH = "/registration/new-request";
const APPLICANT_REQUEST_LEGACY_PATH = "/registration/request";

function App() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, loaded: authLoaded } = useSelector((s) => s.auth);
  const featuresLoaded = useSelector((s) => s.features.loaded);
  const isLoading = useSelector((s) => s?.ui?.loading);
  const company = useSelector((s) => s?.company);
  const subscriptions = useSelector((s) => s.subscriptions);
  const subscribed = useSelector((s) => s.subscriptions?.subscribed);
  const { t, i18n: activeI18n } = useTranslation();
  const storeAlerts = useSelector((s) => s.ui?.globalAlerts || []);
  const authorizedLimitMessage = useSelector(
    (s) => s.authorizedUsers?.limitMessage
  );
  const [dismissedAlertIds, setDismissedAlertIds] = useState([]);
  const [isDynamicTranslation, setIsDynamicTranslation] = useState(false);
  const subscriptionCheckAlertShownRef = useRef(false);
  const dismissedSet = useMemo(
    () => new Set(dismissedAlertIds),
    [dismissedAlertIds]
  );

  //check for dynamic translation loading
  useEffect(() => {
    let active = false;
    let sawRequest = false;
    let timer;

    const finish = () => {
      window.clearTimeout(timer);
      active = false;
      sawRequest = false;
      setIsDynamicTranslation(false);
    };

    const handleLocaleChangeStart = () => {
      window.clearTimeout(timer);
      active = true;
      sawRequest = false;
      setIsDynamicTranslation(true);

      // Cached/static-only pages may not create a tracked request.
      timer = window.setTimeout(finish, 1000);
    };

    const handleRequestState = (event) => {
      if (!active) return;

      const activeCount = Number(event?.detail?.activeCount ?? 0);
      if (!Number.isFinite(activeCount) || activeCount < 0) return;

      if (activeCount > 0) {
        sawRequest = true;
        window.clearTimeout(timer);
        return;
      }

      if (sawRequest) {
        timer = window.setTimeout(finish, 250);
      }
    };

    window.addEventListener(
      "tarh:locale-change-start",
      handleLocaleChangeStart
    );
    window.addEventListener("tarh:request-state", handleRequestState);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener(
        "tarh:locale-change-start",
        handleLocaleChangeStart
      );
      window.removeEventListener("tarh:request-state", handleRequestState);
    };
  }, []);

  const location = useLocation();
  const path = location.pathname || "";
  const hideFooter = path === "/launch";
  const isProfileRoute = path.includes("/profile") || path === "/wallet/recharge";
  const isSubscribeRoute = path === "/subscribe";
  const storedPaymentContext = useMemo(() => readPaymentContext(), [path]);
  const storedUpgradePayment = storedPaymentContext?.upgrade === true;

  // SADAD lock: once a SADAD invoice is generated, keep the user inside /pay/sadad
  // (or /subscribe) until payment is completed and the lock is cleared.
  useEffect(() => {
    let uuid = null;
    try {
      uuid = sessionStorage.getItem("tarh_sadad_tx_uuid");
    } catch (_) { }

    if (!uuid) return;

    // If the user is logged out, do not keep forcing the SADAD route.
    // Clear the lock because it is session-specific.
    if (!user) {
      try {
        sessionStorage.removeItem("tarh_sadad_tx_uuid");
      } catch (_) { }
      return;
    }

    if (subscribed && !storedUpgradePayment) {
      try {
        sessionStorage.removeItem("tarh_sadad_tx_uuid");
      } catch (_) { }
      return;
    }

    const allowed =
      path === "/pay/sadad" ||
      path === "/payment/sadad/result" ||
      path === "/subscribe" ||
      path === "/logout";
    if (!allowed) {
      navigate("/pay/sadad", { replace: true });
    }
  }, [path, navigate, subscribed, user, storedUpgradePayment]);
  // useEffect(() => {
  //   let warningShown = false;
  //   const warn = () => {
  //     if (warningShown) return;
  //     warningShown = true;
  //     try {
  //       console.clear();
  //     } catch (_) {
  //       // ignore
  //     }
  //     console.warn(
  //       "%cتحذير قانوني رسمي\n\nيُمنع منعاً باتاً العبث أو محاولة الدخول إلى الشفرة المصدرية أو بيانات النظام.\nجميع الحقوق محفوظة وفق الأنظمة واللوائح المعمول بها في المملكة العربية السعودية، وأي محاولة للتلاعب ستُعرّضك للمساءلة القانونية الكاملة وقد تُتخذ ضدك إجراءات قضائية صارمة.\nتابع استخدامك بشكل نظامي أو أغلق أدوات التطوير فوراً.",
  //       "color:red;font-size:20px;border-radius:8px;text-align:right;font-family: 'Tajawal', sans-serif !important"
  //     );
  //   };
  //   const detectDevTools = () => {
  //     const threshold = 150;
  //     const widthDiff = Math.abs(window.outerWidth - window.innerWidth);
  //     const heightDiff = Math.abs(window.outerHeight - window.innerHeight);
  //     if (widthDiff > threshold || heightDiff > threshold) {
  //       warn();
  //     }
  //   };
  //   const keyListener = (event) => {
  //     const key = event.key?.toLowerCase?.() || "";
  //     if (
  //       key === "f12" ||
  //       (event.shiftKey && key === "f12") ||
  //       (event.ctrlKey && event.shiftKey && key === "i")
  //     ) {
  //       warn();
  //     }
  //   };
  //   const resizeListener = () => detectDevTools();
  //   const detector = setInterval(detectDevTools, 1000);
  //   window.addEventListener("keydown", keyListener);
  //   window.addEventListener("resize", resizeListener);
  //   detectDevTools();
  //   return () => {
  //     clearInterval(detector);
  //     window.removeEventListener("keydown", keyListener);
  //     window.removeEventListener("resize", resizeListener);
  //   };
  // }, []);

  const lang = localStorage.getItem("lang") || "ar";
  const otpPending = (() => {
    try { return localStorage.getItem('otp_pending') === '1'; } catch (_) { return false; }
  })();
  const isApplicant = isNonSaudiApplicantUser(user);
  const isApprovedApplicant =
    isApplicant && String(user?.registration_status || "").toLowerCase() === "approved";
  const isRestrictedApplicant = isApplicant && !isApprovedApplicant;
  const otpPendingActive = otpPending && !isApplicant;
  const subscriptionStatusLoaded = useSelector(
    (s) => s.subscriptions?.statusLoaded
  );
  const hasPendingBankTransfer =
    !subscribed &&
    subscriptions?.currentPaymentMethod === "Bank Transfer" &&
    subscriptions?.currentPaymentVerificationStatus === "Pending" &&
    !!subscriptions?.currentPaymentReference;
  const hasRejectedBankTransfer =
    !subscribed &&
    subscriptions?.currentPaymentMethod === "Bank Transfer" &&
    subscriptions?.currentPaymentVerificationStatus === "Rejected";
  const hasApprovedBankTransfer =
    subscribed &&
    subscriptions?.currentPaymentMethod === "Bank Transfer" &&
    subscriptions?.currentPaymentVerificationStatus === "Approved";
  const bankTransferStepRedirect = hasPendingBankTransfer
    ? "/bank-transfer/pending"
    : hasRejectedBankTransfer
      ? "/bank-transfer/rejected"
      : null;
  const allowSubscribeAfterRejection = useMemo(() => {
    if (!hasRejectedBankTransfer) {
      return false;
    }
    if (typeof window === "undefined") {
      return false;
    }
    try {
      return (
        sessionStorage.getItem(ALLOW_SUBSCRIBE_AFTER_REJECT_KEY) === "1"
      );
    } catch {
      return false;
    }
  }, [hasRejectedBankTransfer, path]);

  useEffect(() => {
    if (!hasRejectedBankTransfer) {
      if (typeof window === "undefined") {
        return;
      }
      try {
        sessionStorage.removeItem(ALLOW_SUBSCRIBE_AFTER_REJECT_KEY);
      } catch {
        // ignore storage failures
      }
    }
  }, [hasRejectedBankTransfer]);
  const isAuthorizedOnly =
    !!user &&
    (user.role === "authorized_user" ||
      user.auth_context === "authorized_user");

  const canPublish = !!(
    user?.can_publish_tender || user?.can_publish_tenders
  );
  const canSubmit = !!(
    user?.can_submit_offers || user?.can_create_offers_draft
  );
  const canReceive = !!(
    user?.can_view_all_incoming_offers || user?.can_receive_offers
  );
  const hasAnyPermission = isAuthorizedOnly
    ? Object.keys(user || {}).some(
      (key) => key.startsWith("can_") && !!user?.[key]
    )
    : true;
  const ALL_TENDERS_ENABLED = true;
  const COMPANY_LIST_ENABLED = false;
  const allowedModules = Array.isArray(subscriptions?.allowedModules)
    ? subscriptions.allowedModules
    : [];

  const renderSubscriptionGateLoader = () => (
    <div className="p-6">
      <SkeletonLoader rows={5} />
    </div>
  );

  const withPermission = (
    element,
    {
      publish = false,
      submit = false,
      receive = false,
      any = false,
      permissions = [],
    } = {}
  ) => {
    // Company owners and non-authorized users are not restricted by these flags
    if (!isAuthorizedOnly) {
      return element;
    }

    const publishOk = !publish || canPublish;
    const submitOk = !submit || canSubmit;
    const receiveOk = !receive || canReceive;
    const anyOk = !any || hasAnyPermission;

    const permissionList = Array.isArray(permissions) ? permissions : [];
    const permissionOk =
      permissionList.length === 0 ||
      permissionList.some((key) => !!user?.[`can_${key}`]);

    const allowed = publishOk && submitOk && receiveOk && anyOk && permissionOk;

    if (!allowed) {
      return <Navigate to="/dashboard" replace />;
    }

    return element;
  };

  const hasModuleAccess = (moduleKey) => {
    if (!moduleKey) return true;
    if (!subscribed) return false;
    const candidates = String(moduleKey)
      .split(/[|,]/)
      .map((entry) => entry.trim())
      .filter(Boolean);
    if (candidates.length === 0) return true;

    return candidates.some((candidate) => {
      if (!SERVICE_MODULE_KEYS.includes(candidate)) return true;
      if (allowedModules.includes(candidate)) return true;
      return FREE_MODULE_KEYS.includes(candidate);
    });
  };

  const withModuleAccess = (element, moduleKey) => (
    <ModuleAccessGuard allowed={hasModuleAccess(moduleKey)}>
      {element}
    </ModuleAccessGuard>
  );

  const flowState = subscriptions?.flowState || {};
  const subscriptionExpired =
    String(subscriptions?.currentStatus || "").toLowerCase() === "expired";

  // The registration flow (plans, payment, VAT, field of work, authorization letter) is configured
  // per registration type and entity type in Tarh Portal. The backend reports the next pending step;
  // this is the screen the user must see before anything else, or null when nothing is pending.
  const onboardingRedirect = subscriptionStatusLoaded
    ? flowRedirect(flowState, {
        subscribed,
        isExpired: subscriptionExpired,
        bankTransferStepRedirect,
      })
    : null;

  // A registration step page renders only while it is the pending step; otherwise it forwards.
  const renderFlowStep = (stepPath, element) => {
    if (redirectMatchesPath(onboardingRedirect, stepPath)) {
      return element;
    }
    return <Navigate to={onboardingRedirect || "/dashboard"} replace />;
  };

  const renderSubscriptionContent = (element) => {
    if (!user) {
      return <Navigate to="/login" replace />;
    }
    if (isApplicant && !isApprovedApplicant) {
      return <Navigate to={APPLICANT_STATUS_PATH} replace />;
    }
    if (otpPendingActive) {
      return <Navigate to="/login" replace />;
    }

    if (!subscriptionStatusLoaded) {
      return (
        <PrivateRoute>
          {renderSubscriptionGateLoader()}
        </PrivateRoute>
      );
    }

    // 2) Subscription guard
    if (!subscribed) {
      return (
        <Navigate
          to={bankTransferStepRedirect || "/dashboard"}
          replace
        />
      );
    }

    // 3) Registration flow guard: a pending step comes before any module.
    if (onboardingRedirect && !redirectMatchesPath(onboardingRedirect, path)) {
      return <Navigate to={onboardingRedirect} replace />;
    }

    return <PrivateRoute>{element}</PrivateRoute>;
  };

  const renderServicesLanding = () => {
    if (!user) {
      return <Navigate to="/login" replace />;
    }
    if (isApplicant && !isApprovedApplicant) {
      return <Navigate to={APPLICANT_STATUS_PATH} replace />;
    }
    if (otpPendingActive) {
      return <Navigate to="/login" replace />;
    }

    if (!subscriptionStatusLoaded) {
      return (
        <PrivateRoute>
          <ServicesDashboardSkeleton />
        </PrivateRoute>
      );
    }

    if (onboardingRedirect) {
      return <Navigate to={onboardingRedirect} replace />;
    }

    return (
      <PrivateRoute>
        {/* Its own fallback, so the page code loading shows the same module grid placeholder. */}
        <Suspense fallback={<ServicesDashboardSkeleton />}>
          <ServicesDashboard />
        </Suspense>
      </PrivateRoute>
    );
  };

  const renderRootRedirect = () => {
    if (!user) {
      return <Navigate to="/login" replace />;
    }
    if (isApplicant) {
      if (!isApprovedApplicant) {
        return <Navigate to={APPLICANT_STATUS_PATH} replace />;
      }

      if (!subscriptionStatusLoaded) {
        return renderSubscriptionGateLoader();
      }

      const isExpired =
        String(subscriptions?.currentStatus || "").toLowerCase() === "expired";

      return (
        <Navigate
          to={subscribed || isExpired ? HOME_PATH : "/subscribe"}
          replace
        />
      );
    }
    if (otpPendingActive) {
      return <Navigate to="/login" replace />;
    }

    if (!subscriptionStatusLoaded) {
      return renderSubscriptionGateLoader();
    }
    if (onboardingRedirect) {
      return <Navigate to={onboardingRedirect} replace />;
    }
    return <Navigate to={HOME_PATH} replace />;
  };

  const renderSetupRoute = (element) => {
    if (!user) {
      return <Navigate to="/login" replace />;
    }
    if (isApplicant && !isApprovedApplicant) {
      return <Navigate to={APPLICANT_STATUS_PATH} replace />;
    }
    if (otpPendingActive) {
      return <Navigate to="/login" replace />;
    }
    if (!subscriptionStatusLoaded) {
      return renderSubscriptionGateLoader();
    }
    if (!subscribed) {
      return <Navigate to="/subscribe" replace />;
    }
    return <PrivateRoute>{element}</PrivateRoute>;
  };

  const renderBankTransferRoute = () => {
    if (!user) {
      return <Navigate to="/login" replace />;
    }
    if (isApplicant && !isApprovedApplicant) {
      return <Navigate to={APPLICANT_STATUS_PATH} replace />;
    }
    if (otpPendingActive) {
      return <Navigate to="/login" replace />;
    }
    if (!subscriptionStatusLoaded) {
      return (
        <PrivateRoute>
          {renderSubscriptionGateLoader()}
        </PrivateRoute>
      );
    }
    return (
      <PrivateRoute>
        <BankTransferPage />
      </PrivateRoute>
    );
  };

  const renderLoginRoute = () => {
    if (user && isApplicant) {
      if (!isApprovedApplicant) {
        return <Navigate to={APPLICANT_STATUS_PATH} replace />;
      }

      if (!subscriptionStatusLoaded) {
        return renderSubscriptionGateLoader();
      }

      const isExpired =
        String(subscriptions?.currentStatus || "").toLowerCase() === "expired";

      return (
        <Navigate
          to={subscribed || isExpired ? HOME_PATH : "/subscribe"}
          replace
        />
      );
    }
    if (user && !otpPendingActive) {
      if (!subscriptionStatusLoaded) {
        return renderSubscriptionGateLoader();
      }
      const isExpired = String(subscriptions?.currentStatus || "").toLowerCase() === "expired";
      return <Navigate to={subscribed || isExpired ? HOME_PATH : "/subscribe"} replace />;
    }
    return <Login />;
  };

  const renderApplicantRequestRoute = () => {
    if (!user) {
      return <Navigate to="/login" replace />;
    }
    if (otpPendingActive) {
      return <Navigate to="/login" replace />;
    }
    if (!isApplicant) {
      return <Navigate to={APPLICANT_STATUS_PATH} replace />;
    }
    return (
      <PrivateRoute>
        <NewRegistrationRequestPage />
      </PrivateRoute>
    );
  };

  useEffect(() => {
    if (new URLSearchParams(location.search).get("supported") === "1") {
      try {
        sessionStorage.setItem("tarh_support_handoff", "1");
      } catch (_) {
        // Support handoff still works when session storage is unavailable.
      }
    }
  }, [location.search]);

  useEffect(() => {
    dispatch(fetchFeatures());
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;
    const loadUser = async () => {
      dispatch(setLoading(true));
      dispatch(setProgress(0));
      try {
        // A support link has no session yet when the app starts: its page starts
        // the session and loads the customer itself. Checking here as well would
        // answer "nobody signed in" and could overwrite that result.
        if (!window.location.pathname.startsWith("/support/consume/") && window.location.pathname !== "/support/ended") {
          await dispatch(fetchUser()).unwrap();
        }
      } catch (_) {
        // ignore
      } finally {
        if (cancelled) return;
        dispatch(setProgress(100));
        setTimeout(() => {
          dispatch(setLoading(false));
          dispatch(setProgress(0));
        }, 600);
        const lang = localStorage.getItem("lang") || "ar";
        i18n.changeLanguage(lang);
        document.documentElement.setAttribute(
          "dir",
          lang === "ar" ? "rtl" : "ltr"
        );
        document.documentElement.lang = lang;
      }
    };
    loadUser();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  useEffect(() => {
    if (!authLoaded || !user || isRestrictedApplicant) {
      return;
    }
    let cancelled = false;

    const preload = async () => {
      const tasks = [
        dispatch(fetchSubscriptions()).unwrap().catch(() => { }),
        // dispatch(fetchAnnouncements()).unwrap().catch(() => {}),
        dispatch(ensureSubscriptionStatus()).unwrap(),
      ];
      try {
        await Promise.all(tasks);
        dispatch(removeGlobalAlert("subscription-status-offline"));
      } catch (_) {
        if (cancelled) return;
        if (subscriptionCheckAlertShownRef.current) return;
        subscriptionCheckAlertShownRef.current = true;

        Swal.fire({
          title: "Connection issue",
          text: "We couldn't verify your subscription status. Please retry.",
          icon: "warning",
          iconColor: "#dc2626",
          showCancelButton: true,
          confirmButtonText: "Retry",
          cancelButtonText: "Logout",
        }).then(async (result) => {
          if (result.isConfirmed) {
            try {
              await dispatch(checkSubscription()).unwrap();
              dispatch(removeGlobalAlert("subscription-status-offline"));
            } catch {
              dispatch(
                addGlobalAlert({
                  id: "subscription-status-offline",
                  type: "warning",
                  title: "Still offline",
                  message:
                    "We still couldn't verify your subscription status. Please try again in a moment.",
                })
              );
            }
            return;
          }
          dispatch(logoutThunk());
        });
      }
    };

    preload();
    return () => {
      cancelled = true;
    };
  }, [authLoaded, dispatch, isRestrictedApplicant, user]);

  useEffect(() => {
    if (user?.uuid && subscribed && user?.document_verified !== false) {
      dispatch(fetchNotifications());
    }
  }, [
    dispatch,
    user?.uuid,
    subscribed,
    user?.document_verified,
    activeI18n.language,
  ]);

  // const {announcementLoading} = useSelector((s)=>s.announcements.announcementLoading)

  const showDocBanner = false;

  const derivedAlerts = useMemo(() => {
    const alerts = [];
    if (showDocBanner) {
      alerts.push({
        id: "document-verification",
        type: "warning",
        title: t("document_verification_banner_title"),
        message: t("document_verification_banner_desc"),
      });
    }
    if (authorizedLimitMessage) {
      alerts.push({
        id: "subscription-limit",
        type: "danger",
        title: t("subscription_limit_reached"),
        message: authorizedLimitMessage,
      });
    }
    return alerts;
  }, [showDocBanner, authorizedLimitMessage, t]);

  const mergedAlerts = useMemo(() => {
    const map = new Map();
    [...storeAlerts, ...derivedAlerts].forEach((alert) => {
      if (!alert || !alert.id) return;
      map.set(alert.id, alert);
    });
    return Array.from(map.values());
  }, [storeAlerts, derivedAlerts]);

  const visibleAlerts = mergedAlerts.filter(
    (alert) => !dismissedSet.has(alert.id)
  );

  const handleAlertDismiss = (id) => {
    setDismissedAlertIds((prev) =>
      prev.includes(id) ? prev : [...prev, id]
    );
  };

  useEffect(() => {
    if (!showDocBanner) {
      setDismissedAlertIds((prev) =>
        prev.includes("document-verification")
          ? prev.filter((id) => id !== "document-verification")
          : prev
      );
    }
  }, [showDocBanner]);

  useEffect(() => {
    if (!authorizedLimitMessage) {
      setDismissedAlertIds((prev) =>
        prev.includes("subscription-limit")
          ? prev.filter((id) => id !== "subscription-limit")
          : prev
      );
    }
  }, [authorizedLimitMessage]);

  const authorizationPending = useMemo(() => {
    if (!authLoaded || !user) return false;
    if (isViewerMember(user)) return false;

    const hasSubscription = !!subscribed;
    const hasUploadedAuthorization =
      user.Registration_Certificate === true ||
      !!user.authorizationUrl;

    return (
      hasSubscription &&
      hasUploadedAuthorization &&
      user.document_verified === false
    );
  }, [authLoaded, user, subscribed]);

  // Handoff establishes authentication itself. It must render before the auth
  // gate and must not remount when the selected language changes mid-exchange.
  if (path.startsWith("/support/consume/") || path === "/support/ended") {
    return (
      <Routes>
        <Route path="/support/consume/:token" element={<SupportHandoffPage />} />
        <Route path="/support/ended" element={<SupportEndedPage />} />
      </Routes>
    );
  }

  // Wait for the feature switches too, so hidden features never flash on the first render.
  if (!authLoaded || !featuresLoaded) {
    return (
      <div className="flex items-center justify-center h-screen  dark:bg-slate-800 dark:text-white bg-gray-100">
        <GlobalLoader />
      </div>
    );
  }

  // Subscription gating happens via routes below

  const formatArEnNumber = (n, grouping = false, showCurrency = false) => {
    if (n === null || n === undefined || isNaN(n)) return "0";

    const locale = lang === "en" ? "en-US" : "ar-SA-u-nu-latn";

    const options = {
      useGrouping: grouping,
      style: "decimal",
    };

    const formatted = new Intl.NumberFormat(locale, options).format(n);

    if (showCurrency) {
      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            color: "#1B8354", // number + icon color
            fontWeight: 700,
          }}
        >
          {lang === "ar" ? (
            <>
              <span>{formatted}</span>
              <SaudiRiyal size={18} color="#1B8354" />
            </>
          ) : (
            <>
              <span>{formatted}</span>
              <SaudiRiyal size={18} color="#1B8354" />
            </>
          )}
        </span>
      );
    }

    return (
      <span style={{ color: "#1B8354", fontWeight: 700 }}>{formatted}</span>
    );
  };

  const formatDateByLocale = (dateInput, formatOptions = null) => {
    if (!dateInput) return null;

    const locale = lang === "en" ? "en-US" : "en-SA";
    const defaultOptions = {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    };
    const options =
      formatOptions && Object.keys(formatOptions || {}).length > 0
        ? { ...formatOptions }
        : defaultOptions;

    const normalizeIsoCandidate = (value) => {
      if (typeof value !== "string") {
        return value;
      }
      const trimmed = value.trim();
      if (!trimmed) return trimmed;
      if (trimmed.includes("T")) {
        return trimmed;
      }
      if (trimmed.includes(" ")) {
        return trimmed.replace(" ", "T");
      }
      return trimmed;
    };

    const parseLegacyTenderDate = (value) => {
      const parts = value?.match(
        /(\d{2})-(\w{3})-(\d{4}) (\d{2}):(\d{2}):(\d{2}) (AM|PM)/
      );
      if (!parts) return null;

      const monthMap = {
        Jan: 0,
        Feb: 1,
        Mar: 2,
        Apr: 3,
        May: 4,
        Jun: 5,
        Jul: 6,
        Aug: 7,
        Sep: 8,
        Oct: 9,
        Nov: 10,
        Dec: 11,
      };

      let [, day, month, year, hours, minutes, seconds, ampm] = parts;
      hours = parseInt(hours, 10);
      if (ampm === "PM" && hours !== 12) {
        hours += 12;
      }
      if (ampm === "AM" && hours === 12) {
        hours = 0;
      }

      const dateObj = new Date(
        parseInt(year, 10),
        monthMap[month],
        parseInt(day, 10),
        hours,
        parseInt(minutes, 10),
        parseInt(seconds, 10)
      );

      return Number.isNaN(dateObj.getTime()) ? null : dateObj;
    };

    const resolveDate = (value) => {
      if (!value) return null;

      if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
      }

      if (typeof value === "number") {
        const fromNumber = new Date(value);
        return Number.isNaN(fromNumber.getTime()) ? null : fromNumber;
      }

      if (typeof value === "string") {
        const isoCandidate = normalizeIsoCandidate(value);
        const fromIso = new Date(isoCandidate);
        if (!Number.isNaN(fromIso.getTime())) {
          return fromIso;
        }
        const fallback = new Date(value);
        if (!Number.isNaN(fallback.getTime())) {
          return fallback;
        }
        return parseLegacyTenderDate(value);
      }

      return null;
    };

    const dateObj = resolveDate(dateInput);
    if (!dateObj) return null;

    return new Intl.DateTimeFormat(locale, options).format(dateObj);
  };

  const getModuleTitle = (moduleKey) =>
    getServiceModuleName(moduleKey, lang, "dashboard", t);

  const onSetupRoute = path === "/vat" || path === "/subscribe";
  const showAlertBar = !onSetupRoute && visibleAlerts.length > 0;

  return (
    <div className="app-shell flex flex-col bg-white text-gray-900 dark:bg-slate-700 dark:text-white transition-colors duration-300">
      <GlobalLoader visible={isDynamicTranslation} />
      <AttachmentViewer />
      <Navbar />
      {user && <SupportSessionBanner />}
      {showAlertBar && (
        <div className="mt-16 lg:mt-24 -mb-15 lg:-mb-20">
          <AlertBar alerts={visibleAlerts} onDismiss={handleAlertDismiss} />
        </div>
      )}

      <main className="flex-grow container mx-auto pt-26">
        {authorizationPending && !isProfileRoute ? (
          <Suspense
            fallback={
              <div className="p-4">
                <SkeletonLoader rows={4} />
              </div>
            }
          >
            <PrivateRoute>
              <AuthorizationPendingPage
                onRefresh={() => {
                  dispatch(fetchUser());
                }}
              />
            </PrivateRoute>
          </Suspense>
        ) : (
          <Suspense
            fallback={
              <div className="p-4">
                <SkeletonLoader rows={4} />
              </div>
            }
          >
            <Routes key={activeI18n.resolvedLanguage || activeI18n.language}>
              {/* Root */}
              <Route
                path="/"
                element={renderRootRedirect()}
              />

              {/* Public */}
              <Route
                path="/login"
                element={renderLoginRoute()}
              />
              <Route path="/login/non-saudi" element={<Navigate to="/login?type=non_saudi" replace />} />
              <Route path="/register" element={<CompanyTypeSelectionPage />} />
              <Route path="/register/saudi" element={<Register />} />
              <Route path="/register/non-saudi" element={<NonSaudiRegistrationPage />} />
              <Route path="/register/non-saudi/verification-complete" element={<NonSaudiEmailVerificationCompletePage />} />
              <Route path="/registration/status" element={<RegistrationStatusPage />} />
              <Route path={APPLICANT_REQUEST_PATH} element={renderApplicantRequestRoute()} />
              <Route
                path={APPLICANT_REQUEST_LEGACY_PATH}
                element={
                  <Navigate
                    to={{ pathname: APPLICANT_REQUEST_PATH, search: location.search }}
                    replace
                  />
                }
              />
              <Route path="/forgot" element={<Forgot />} />
              <Route path="/reset" element={<Reset />} />

              {/* Private */}
              <Route
                path="/dashboard"
                element={renderServicesLanding()}
              />
              <Route
                path="/tenders-dashboard"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    <Dashboard formatArEnNumber={formatArEnNumber} />,
                    "tenders_offers_management"
                  )
                )}
              />
              <Route
                path="/offers-dashboard"
                element={renderSubscriptionContent(
                  withModuleAccess(
                      withPermission(
                        <OffersDashboard formatArEnNumber={formatArEnNumber} />,
                        {
                          permissions: [
                            "view_all_submissions",
                            "view_my_submissions",
                            "create_offers_draft",
                            "submit_offers",
                            "move_public_tender_to_my_submissions",
                            "request_to_join_tender",
                          ],
                        }
                      ),
                      "offers_management"
                    )
                  )}
              />
              <Route
                path="/negotiation-dashboard"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    <NegotiationDashboard formatArEnNumber={formatArEnNumber} />,
                    "negotiation_award_management"
                  )
                )}
              />
              <Route
                path="/financial-claims-dashboard"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    <FinancialClaimsDashboard
                      formatArEnNumber={formatArEnNumber}
                    />,
                    "financial_claims_management"
                  )
                )}
              />
              <Route
                path="/approvals-dashboard"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    <ApprovalDashboard
                      formatArEnNumber={formatArEnNumber}
                    />,
                    "approvals_requests_management"
                  )
                )}
              />
              <Route
                path="/tender-form"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<TenderForm />, {
                      permissions: ["create_tender", "publish_tender"],
                    }),
                    "tenders_offers_management"
                  )
                )}
              />

              <Route
                path="/draft/:recId/edit"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<TenderForm />, {
                      permissions: [
                        "create_tender",
                        "edit_tender_general_info",
                        "publish_tender",
                      ],
                    }),
                    "tenders_offers_management"
                  )
                )}
              />
              {/* Home: public tenders for every subscriber, with the public navbar. No module guard:
                  the page itself turns view-only for companies without a tender module. */}
              <Route
                path={HOME_PATH}
                element={
                  ALL_TENDERS_ENABLED
                    ? renderSubscriptionContent(<AllTendersLanding />)
                    : <Navigate to="/dashboard" replace />
                }
              />
              <Route
                path="/all-tenders"
                element={
                  ALL_TENDERS_ENABLED
                    ? renderSubscriptionContent(
                      withModuleAccess(
                        <TenderDashboard />,
                        "offers_management|tenders_offers_management"
                      )
                    )
                    : <Navigate to="/dashboard" replace />
                }
              />

              <Route
                path="/my-tenders"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<TenderDashboard />, {
                      permissions: ["view_published_tenders", "view_my_submitted_competitions"],
                    }),
                    "tenders_offers_management"
                  )
                )}
              />

              <Route
                path="/my-offers"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<TenderDashboard />, {
                      permissions: ["view_all_submissions", "view_my_submissions"],
                    }),
                    "offers_management"
                  )
                )}
              />
              <Route
                path="/proposal-approvals"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<ProposalDashboard />, {
                      permissions: ["accept_reject_invitations"],
                    }),
                    "proposal_approvals_management"
                  )
                )}
              />
              <Route
                path="/incoming-invitations"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<IncomingInvitations />, {
                      permissions: ["accept_reject_invitations"],
                    }),
                    "proposal_approvals_management"
                  )
                )}
              />
              <Route
                path="/incoming-participation-requests"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<IncomingParticipationRequests />, {
                      permissions: ["publish_tender"],
                    }),
                    "approvals_requests_management"
                  )
                )}
              />
              <Route
                path="/incoming-negotiation-requests"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<IncomingNegotiations />, {
                      permissions: [
                        "view_incoming_negotiations_for_review",
                        "accept_negotiation",
                        "submit_offers",
                        "create_offers_draft",
                      ],
                    }),
                    "negotiation_award_management"
                  )
                )}
              />
              <Route
                path="/financial-claims"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<FinancialClaims />, {
                      permissions: ["view_financial_claims"],
                    }),
                    "financial_claims_management"
                  )
                )}
              />
              <Route
                path="/financial-claims/new"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<FinancialClaimForm />, {
                      permissions: ["create_financial_claim"],
                    }),
                    "financial_claims_management"
                  )
                )}
              />
              <Route
                path="/financial-claims/bank-accounts"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<BankAccountManagement />, {
                      permissions: ["bank_account_management"],
                    }),
                    "financial_claims_management"
                  )
                )}
              />
              <Route
                path="/financial-claims/awarded-tenders"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<AwardedTendersToMe />, {
                      permissions: ["create_financial_claim"],
                    }),
                    "financial_claims_management"
                  )
                )}
              />
              <Route
                path="/financial-claims/:id"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<FinancialClaimForm />, {
                      permissions: ["create_financial_claim"],
                    }),
                    "financial_claims_management"
                  )
                )}
              />
              <Route
                path="/publisher-negotiation-responses"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<PublisherNegotiationResponses />, {
                      permissions: ["view_pending_supplier_negotiations"],
                    }),
                    "negotiation_award_management"
                  )
                )}
              />
              <Route
                path="/publisher-negotiations/:id"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<PublisherNegotiationResponseDetails />, {
                      permissions: ["view_pending_supplier_negotiations"],
                    }),
                    "negotiation_award_management"
                  )
                )}
              />
              <Route
                path="/incoming-financial-claims"
                element={renderSubscriptionContent(
                  withModuleAccess(
                      withPermission(<IncomingFinancialClaims />, {
                        permissions: [
                          "review_financial_claim",
                          "review_certificate_completion",
                          "approve_claim_amount",
                          "approve_claim_payment_processing",
                          "approve_claim_payment",
                      ],
                    }),
                    "financial_claims_management"
                  )
                )}
              />
              <Route
                path="/incoming-financial-claims/:id"
                element={renderSubscriptionContent(
                  withModuleAccess(
                      withPermission(<IncomingFinancialClaimDetails />, {
                        permissions: [
                          "review_financial_claim",
                          "review_certificate_completion",
                          "approve_claim_amount",
                          "approve_claim_payment_processing",
                          "approve_claim_payment",
                      ],
                    }),
                    "financial_claims_management"
                  )
                )}
              />
              <Route
                path="/tender/:recId/view"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    <TenderInfo
                      formatArEnNumber={formatArEnNumber}
                      formatDateByLocale={formatDateByLocale}
                    />,
                    "tenders_offers_management|offers_management|tender_viewing"
                  )
                )}
              />

              <Route
                path="/mytender/:recId/view"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(
                        <TenderInfo
                          formatArEnNumber={formatArEnNumber}
                          formatDateByLocale={formatDateByLocale}
                        />,
                        {
                          permissions: [
                            "view_published_tenders",
                            "view_my_submitted_competitions",
                          ],
                        }
                      ),
                      "tenders_offers_management"
                    )
                )}
              />
              <Route
                path="/mytender/:recId/edit"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<TenderForm />, {
                      permissions: [
                        "edit_tender_general_info",
                        "publish_tender",
                        "create_tender",
                      ],
                    }),
                    "tenders_offers_management"
                  )
                )}
              />
              <Route
                path="/mytender/:recId/extend"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<TenderForm />, {
                      permissions: ["extend_tender_duration"],
                    }),
                    "tenders_offers_management"
                  )
                )}
              />
              <Route
                path="/mytender/:recId/edit-boq"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<TenderForm />, {
                      permissions: ["edit_boq"],
                    }),
                    "tenders_offers_management"
                  )
                )}
              />
              <Route
                path="/myoffer/:recId/view"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(
                      <TenderInfo
                        formatArEnNumber={formatArEnNumber}
                        formatDateByLocale={formatDateByLocale}
                      />,
                      { submit: true }
                    ),
                    "offers_management"
                  )
                )}
              />

              <Route
                path="/submitOffer/:recId/active"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(
                      <ViewerMemberOfferGuard user={user}>
                        <TenderInfo
                          formatArEnNumber={formatArEnNumber}
                          formatDateByLocale={formatDateByLocale}
                        />
                      </ViewerMemberOfferGuard>,
                      { submit: true }
                    ),
                    "offers_management"
                  )
                )}
              />
              <Route
                path="/mytender/:recId/negotiation"
                element={renderSubscriptionContent(
                    withModuleAccess(
                      withPermission(<NegotiationStage />, {
                      permissions: ["create_negotiation_offer_draft"],
                      }),
                      "negotiation_award_management"
                    )
                )}
              />
              <Route
                path="/mytender/:recId/award"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<TenderAwarding />, {
                      permissions: ["award_supplier"],
                    }),
                    "negotiation_award_management"
                  )
                )}
              />
              <Route
                path="/mytender/:recId/award/importing-entity"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<ImportingEntitySelection />, {
                      permissions: ["award_supplier"],
                    }),
                    "negotiation_award_management"
                  )
                )}
              />
              <Route
                path="/tenders-pending-awarding"
                element={renderSubscriptionContent(
                  withModuleAccess(
                      withPermission(<TenderAwardingList mode="pending" />, {
                        permissions: ["tenders_pending_awarding"],
                      }),
                    "negotiation_award_management"
                  )
                )}
              />
              <Route
                path="/awarded-tenders"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<TenderAwardingList mode="awarded" />, {
                      permissions: ["view_award_approvals"],
                    }),
                    "negotiation_award_management"
                  )
                )}
              />
              <Route
                path="/tenders-awarded-to-me"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<TenderAwardingList mode="awarded-to-me" />, {
                      permissions: [
                        "view_tenders_awarded_to_me",
                        "view_award_approvals",
                      ],
                    }),
                    "negotiation_award_management"
                  )
                )}
              />

              <Route
                path="/submitOffer/:recId/draft"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(
                      <ViewerMemberOfferGuard user={user}>
                        <TenderInfo
                          formatArEnNumber={formatArEnNumber}
                          formatDateByLocale={formatDateByLocale}
                        />
                      </ViewerMemberOfferGuard>,
                      { submit: true }
                    ),
                    "offers_management"
                  )
                )}
              />
              <Route
                path="/negotiations/:id"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<NegotiationOfferDetails />, {
                      permissions: ["submit_offers", "create_offers_draft"],
                    }),
                    "negotiation_award_management"
                  )
                )}
              />

              <Route
                path="/incoming-offers-price-analysis-dashboard"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<IncomingOffersPriceAnalysisDashboard />, {
                      permissions: ["view_all_incoming_offers"],
                    }),
                    "incoming_offers_price_analysis_management"
                  )
                )}
              />
              <Route
                path="/received-proposals"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<ReceivedProposal />, {
                      permissions: ["view_all_incoming_offers"],
                    }),
                    "incoming_offers_price_analysis_management"
                  )
                )}
              />
              <Route
                path="/tender-approvals"
                element={renderSubscriptionContent(
                    withModuleAccess(
                      withPermission(<TenderApprovals />, {
                      permissions: ["publish_tender"],
                      }),
                      "approvals_requests_management"
                    )
                )}
              />
              <Route
                path="/tender-approvals/:recId/view"
                element={renderSubscriptionContent(
                    withModuleAccess(
                      withPermission(<TenderForm />, {
                      permissions: ["publish_tender"],
                      }),
                      "approvals_requests_management"
                    )
                )}
              />
              <Route
                path="/contracts-approvals"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<ContractsApprovals />, {
                      permissions: [
                        "view_contract_approval_requests",
                        "approve_reject_contract",
                      ],
                    }),
                    "contracts_management"
                  )
                )}
              />

              <Route
                path="/report/tender/:recId/offers-analysis"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<OffersAnalysisPage />, {
                      permissions: ["view_all_incoming_offers"],
                    }),
                    "tenders_offers_management"
                  )
                )}
              />

              <Route
                path="/profile"
                element={
                  user ? (
                    <PrivateRoute>
                      <ProfilePage />
                    </PrivateRoute>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/wallet/recharge"
                element={
                  user ? (
                    <PrivateRoute>
                      <RechargeWalletPage />
                    </PrivateRoute>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route path="/wallet/recharge/payment" element={user ? <PrivateRoute><WalletRechargePaymentPage /></PrivateRoute> : <Navigate to="/login" replace />} />
              <Route path="/wallet/recharge/sadad" element={user ? <PrivateRoute><WalletSadadPage /></PrivateRoute> : <Navigate to="/login" replace />} />
              <Route path="/wallet/recharge/result" element={user ? <PrivateRoute><WalletRechargeResultPage /></PrivateRoute> : <Navigate to="/login" replace />} />
              <Route path="/wallet/recharge/submitted" element={user ? <PrivateRoute><WalletRechargeResultPage /></PrivateRoute> : <Navigate to="/login" replace />} />
              <Route
                path="/delegation-dashboard"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    <DelegationDashboard
                      title={getModuleTitle("delegation_permissions_management")}
                      subtitle={t("delegation_dashboard_placeholder")}
                    />,
                    "delegation_permissions_management"
                  )
                )}
              />
              <Route
                path="/delegates/new"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<CreateDelegate />, { any: true }),
                    "delegation_permissions_management"
                  )
                )}
              />
              <Route
                path="/delegates/permissions"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<PermissionManagement />, { any: true }),
                    "delegation_permissions_management"
                  )
                )}
              />
              <Route
                path="/pay"
                element={
                  user ? (
                    isApplicant && !isApprovedApplicant ? (
                      <Navigate to={APPLICANT_STATUS_PATH} replace />
                    ) : otpPendingActive ? (
                      <Navigate to="/login" replace />
                    ) : !subscriptionStatusLoaded ? (
                      <PrivateRoute>
                        {renderSubscriptionGateLoader()}
                      </PrivateRoute>
                    ) : (
                      (() => {
                        const allowUpgradePayment =
                          location.state?.upgrade === true ||
                          location.state?.type === "modules" ||
                          Array.isArray(location.state?.module_ids) ||
                          storedPaymentContext?.upgrade === true ||
                          storedPaymentContext?.type === "modules";
                        if (subscribed && !allowUpgradePayment) {
                          return <Navigate to="/dashboard" replace />;
                        }

                        if (hasPendingBankTransfer) {
                          return (
                            <Navigate
                              to="/bank-transfer/pending"
                              replace
                            />
                          );
                        }

                        return (
                          <PrivateRoute>
                            <PaymentWidget />
                          </PrivateRoute>
                        );
                      })()
                    )
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/pay/apple"
                element={
                  user ? (
                    isApplicant && !isApprovedApplicant ? (
                      <Navigate to={APPLICANT_STATUS_PATH} replace />
                    ) : otpPendingActive ? (
                      <Navigate to="/login" replace />
                    ) : !subscriptionStatusLoaded ? (
                      <PrivateRoute>
                        {renderSubscriptionGateLoader()}
                      </PrivateRoute>
                    ) : (
                      (() => {
                        const allowUpgradePayment =
                          location.state?.upgrade === true ||
                          storedPaymentContext?.upgrade === true;
                        if (subscribed && !allowUpgradePayment) {
                          return <Navigate to="/dashboard" replace />;
                        }

                        if (hasPendingBankTransfer) {
                          return (
                            <Navigate
                              to="/bank-transfer/pending"
                              replace
                            />
                          );
                        }

                        return (
                          <PrivateRoute>
                            <ApplePayPage />
                          </PrivateRoute>
                        );
                      })()
                    )
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/pay/sadad"
                element={
                  user ? (
                    isApplicant && !isApprovedApplicant ? (
                      <Navigate to={APPLICANT_STATUS_PATH} replace />
                    ) : otpPendingActive ? (
                      <Navigate to="/login" replace />
                    ) : !subscriptionStatusLoaded ? (
                      <PrivateRoute>
                        {renderSubscriptionGateLoader()}
                      </PrivateRoute>
                    ) : (
                      (() => {
                        const allowUpgradePayment =
                          location.state?.upgrade === true ||
                          location.state?.type === "modules" ||
                          Array.isArray(location.state?.module_ids) ||
                          storedPaymentContext?.upgrade === true ||
                          storedPaymentContext?.type === "modules";
                        if (subscribed && !allowUpgradePayment) {
                          return <Navigate to="/dashboard" replace />;
                        }

                        if (hasPendingBankTransfer) {
                          return (
                            <Navigate to="/bank-transfer/pending" replace />
                          );
                        }

                        return (
                          <PrivateRoute>
                            <SadadPayPage />
                          </PrivateRoute>
                        );
                      })()
                    )
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/bank-transfer"
                element={<Navigate to="/bank-transfer/form" replace />}
              />
              <Route
                path="/bank-transfer/:step"
                element={renderBankTransferRoute()}
              />
              {/* Registration flow steps. Each renders only while it is the pending step (see renderFlowStep). */}
              <Route
                path="/vat"
                element={renderSetupRoute(renderFlowStep("/vat", <VatPage />))}
              />
              <Route
                path="/declaration"
                element={renderSetupRoute(renderFlowStep("/declaration", <DeclarationGuardPage />))}
              />
              <Route
                path="/declaration/pending"
                element={renderSetupRoute(renderFlowStep("/declaration/pending", <DeclarationPendingPage />))}
              />
              <Route
                path="/field-of-work-classification"
                element={renderSetupRoute(
                  redirectMatchesPath(onboardingRedirect, "/field-of-work-classification") || !onboardingRedirect ? (
                    <FieldOfWorkClassificationPage />
                  ) : (
                    <Navigate to={onboardingRedirect} replace />
                  )
                )}
              />
              <Route
                path="/work-classification-management"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    <FieldOfWorkClassificationPage />,
                    "work_classification_management"
                  )
                )}
              />
              <Route
                path="/payment/result"
                element={
                  user ? (
                    isApplicant && !isApprovedApplicant ? (
                      <Navigate to={APPLICANT_STATUS_PATH} replace />
                    ) : otpPendingActive ? (
                      <Navigate to="/login" replace />
                    ) : !subscriptionStatusLoaded ? (
                      <PrivateRoute>
                        {renderSubscriptionGateLoader()}
                      </PrivateRoute>
                    ) : (
                      <PrivateRoute>
                        <PaymentResult />
                      </PrivateRoute>
                    )
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/payment/apple/result"
                element={
                  user ? (
                    isApplicant && !isApprovedApplicant ? (
                      <Navigate to={APPLICANT_STATUS_PATH} replace />
                    ) : otpPendingActive ? (
                      <Navigate to="/login" replace />
                    ) : !subscriptionStatusLoaded ? (
                      <PrivateRoute>
                        {renderSubscriptionGateLoader()}
                      </PrivateRoute>
                    ) : (
                      <PrivateRoute>
                        <ApplePayResult />
                      </PrivateRoute>
                    )
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/payment/sadad/result"
                element={
                  user ? (
                    isApplicant && !isApprovedApplicant ? (
                      <Navigate to={APPLICANT_STATUS_PATH} replace />
                    ) : otpPendingActive ? (
                      <Navigate to="/login" replace />
                    ) : !subscriptionStatusLoaded ? (
                      <PrivateRoute>
                        {renderSubscriptionGateLoader()}
                      </PrivateRoute>
                    ) : (
                      <PrivateRoute>
                        <SadadPaymentResult />
                      </PrivateRoute>
                    )
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />

              {/* <Route
                path="/paytest"
                element={                 
                      <PaymentWidget />
                }
              /> */}



              <Route
                path="/preffered-supplier"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(
                      <PreferredSuppliers
                        formatArEnNumber={formatArEnNumber}
                        formatDateByLocale={formatDateByLocale}
                      />,
                      { any: true }
                    ),
                    "preferred_vendors_management"
                  )
                )}
              />

              <Route
                path="/company-list"
                element={
                  COMPANY_LIST_ENABLED
                    ? renderSubscriptionContent(
                      withModuleAccess(
                        withPermission(<CompanyListPage />, {
                          permissions: ["publish_tender"],
                        }),
                        "companies_management"
                      )
                    )
                    : <Navigate to="/dashboard" replace />
                }
              />
              <Route
                path="/importing-entities"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    <ImportingEntitiesListPage
                      mode="importing-entities"
                      allowPreferredSupplierAction={false}
                    />,
                    "Importing_Entity_Plan"
                  )
                )}
              />
              <Route
                path="/importing-entities/requests"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    <ImportRequestsList />,
                    "Importing_Entity_Plan"
                  )
                )}
              />
              <Route
                path="/importing-entities/requests/new"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    <ImportRequestForm />,
                    "Importing_Entity_Plan"
                  )
                )}
              />
              <Route
                path="/importing-entities/requests/:publicId"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    <ImportRequestDetails />,
                    "Importing_Entity_Plan"
                  )
                )}
              />

              <Route
                path="/premium-subscribers"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<PremiumSubscribersList />, { any: true }),
                    "companies_management"
                  )
                )}
              />

              <Route
                path="/notifications"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    <NotificationPage />,
                    "notifications_management"
                  )
                )}
              />

              <Route
                path="/subscription-management"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    <SubscriptionManagementPage />,
                    "subscription_management"
                  )
                )}
              />
              <Route
                path="/upgrade"
                element={renderSubscriptionContent(
                  <UpgradePlansPage />
                )}
              />

              <Route
                path="/communication-management"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    <CommunicationPage />,
                    "communication_management"
                  )
                )}
              />

              <Route
                path="/contracts-management"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    <Navigate to="/contracts-dashboard" replace />,
                    "contracts_management"
                  )
                )}
              />
              <Route
                path="/contracts-dashboard"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    <ContractsDashboard formatArEnNumber={formatArEnNumber} />,
                    "contracts_management"
                  )
                )}
              />
              <Route
                path="/contracts-management/create"
                element={renderSubscriptionContent(
                    withModuleAccess(
                      withPermission(<ContractForm />, {
                      permissions: ["create_contract_draft"],
                      }),
                      "contracts_management"
                    )
                )}
              />
              <Route
                path="/contracts-management/issued"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<IssuedContracts />, {
                      permissions: ["view_issued_contracts"],
                    }),
                    "contracts_management"
                  )
                )}
              />
              <Route
                path="/contracts-management/:contractId"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<ContractForm />, {
                      permissions: [
                        "view_issued_contracts",
                        "review_incoming_contract",
                        "view_approved_contracts",
                        "view_contract_approval_requests",
                      ],
                    }),
                    "contracts_management"
                  )
                )}
              />
              <Route
                path="/financial-claims/contracts/:contractId"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(
                      <ContractForm
                        claimsViewMode={true}
                        supplierListPath="/financial-claims/contracts/incoming"
                        issuerListPath="/financial-claims/contracts/approved"
                      />,
                      {
                        permissions: [
                          "view_issued_contracts",
                          "review_incoming_contract",
                          "view_approved_contracts",
                          "view_contract_approval_requests",
                        ],
                      }
                    ),
                    "financial_claims_management"
                  )
                )}
              />
              <Route
                path="/contracts-management/incoming"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<IncomingContracts />, {
                      permissions: ["review_incoming_contract"],
                    }),
                    "contracts_management"
                  )
                )}
              />
              <Route
                path="/financial-claims/contracts/incoming"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(
                      <IncomingContracts
                        detailBasePath="/financial-claims/contracts"
                      />,
                      {
                        permissions: ["review_incoming_contract"],
                      }
                    ),
                    "financial_claims_management"
                  )
                )}
              />
              <Route
                path="/contracts-management/approved"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<ApprovedContracts />, {
                      permissions: ["view_approved_contracts"],
                    }),
                    "contracts_management"
                  )
                )}
              />
              <Route
                path="/financial-claims/contracts/approved"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(
                      <ApprovedContracts
                        detailBasePath="/financial-claims/contracts"
                      />,
                      {
                        permissions: ["view_approved_contracts"],
                      }
                    ),
                    "financial_claims_management"
                  )
                )}
              />

              <Route
                path="/platform-guide"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    <ExternalRedirectPage
                      url="http://tarhplatform.com/assets/%D9%84%D8%AF%D9%84%D9%8A%D9%84%20%D8%A7%D9%84%D8%AA%D8%B9%D8%B1%D9%8A%D9%81%D9%8A%20%D9%84%D9%85%D9%86%D8%B5%D8%A9%20%D8%B7%D8%B1%D8%AD.pdf"
                      titleKey="platform_guide_redirect_title"
                      descriptionKey="platform_guide_redirect_message"
                      actionLabelKey="platform_guide_redirect_action"
                    />,
                    "platform_guide"
                  )
                )}
              />

              <Route
                path="/subscribe"
                element={
                  user ? (
                    isApplicant && !isApprovedApplicant ? (
                      <Navigate to={APPLICANT_STATUS_PATH} replace />
                    ) : otpPendingActive ? (
                      <Navigate to="/login" replace />
                    ) : (!subscriptionStatusLoaded ? (
                      <PrivateRoute>
                        {renderSubscriptionGateLoader()}
                      </PrivateRoute>
                    ) : (
                      (() => {
                        // If already subscribed via other methods, go straight to dashboard.
                        // For Bank Transfer with "Approved" status, keep the user on the
                        // subscription page so the approval modal can be shown.
                        const allowSubscribeFromRejected =
                          allowSubscribeAfterRejection && hasRejectedBankTransfer;

                        if (bankTransferStepRedirect && !allowSubscribeFromRejected) {
                          return <Navigate to={bankTransferStepRedirect} replace />;
                        }

                        // A later registration step is pending (VAT, field of work, letter): go there first.
                        if (subscribed && onboardingRedirect && !redirectMatchesPath(onboardingRedirect, "/subscribe")) {
                          return <Navigate to={onboardingRedirect} replace />;
                        }

                        if (subscribed && !hasApprovedBankTransfer) {
                          return <Navigate to="/dashboard" replace />;
                        }

                        return (
                          <PrivateRoute>
                            <SubscriptionPage />
                          </PrivateRoute>
                        );
                      })()
                    ))
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />

              <Route
                path="/launch"
                element={
                  <PrivateRoute>
                    <LandingPage />
                  </PrivateRoute>
                }
              />
            </Routes>
          </Suspense>
        )}
      </main>
      {!hideFooter && <Footer />}
      <MobileBottomNav />
      <PullToRefresh />
      <ScrollToTop />
    </div>
  );
}
export default App;
